// TaskActivityHistory: Collapsible activity timeline for TaskDetailModal
// Loads and displays all logged events for a mission (status changes, field inputs,
// reassignments, replanifications, auto-overdue) with actor info and timestamps.
// UPDATED: Replaced broken CreditCard alias with PenLine for text_input_update icon
// UPDATED: Removed activity count badge from accordion trigger
// UPDATED: Added 'deletion' to ACTION_CONFIG so deletion logs render in the timeline
// UPDATED: Query errors are now surfaced instead of silently showing empty state

import { useState, useEffect, useCallback } from 'react';
import { ChevronDown, History, CheckCircle2, AlertCircle, Clock, UserCheck, Calendar, PenLine, Bot, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface ActivityLog {
  id: string;
  task_id: string;
  occurrence_date: string | null;
  action_type: string;
  actor_id: string | null;
  actor_name: string | null;
  actor_type: 'user' | 'system';
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

interface TaskActivityHistoryProps {
  taskId: string;
  occurrenceDate?: string | null;
}

const ACTION_CONFIG: Record<string, {
  icon: typeof History;
  dotColor: string;
  label: (log: ActivityLog) => string;
}> = {
  completion: {
    icon: CheckCircle2,
    dotColor: 'bg-emerald-500',
    label: () => 'Mission terminée',
  },
  status_change: {
    icon: Clock,
    dotColor: 'bg-amber-500',
    label: (log) => {
      const newStatus = log.new_value?.status as string | undefined;
      const labels: Record<string, string> = {
        not_started: 'Remise en "Non démarrée"',
        completed: 'Marquée comme terminée',
        overdue: 'Marquée en retard',
      };
      return labels[newStatus ?? ''] ?? 'Statut modifié';
    },
  },
  text_input_update: {
    icon: PenLine,
    dotColor: 'bg-blue-500',
    label: (log) => {
      const fieldTitle = log.metadata?.field_title as string | undefined;
      return fieldTitle ? `Champ "${fieldTitle}" renseigné` : 'Champ de saisie renseigné';
    },
  },
  reassignment: {
    icon: UserCheck,
    dotColor: 'bg-sky-500',
    label: () => 'Mission réattribuée',
  },
  reschedule: {
    icon: Calendar,
    dotColor: 'bg-slate-500',
    label: (log) => {
      const newTime = log.new_value?.scheduled_time as string | undefined;
      return newTime ? `Replanifiée à ${newTime}` : 'Heure modifiée';
    },
  },
  overdue_auto: {
    icon: AlertCircle,
    dotColor: 'bg-red-500',
    label: () => 'Passée en retard automatiquement',
  },
  deletion: {
    icon: Trash2,
    dotColor: 'bg-gray-500',
    label: (log) => {
      const scope = log.metadata?.scope as string | undefined;
      if (scope === 'all') return 'Série entière supprimée';
      if (scope === 'from') return 'Série supprimée à partir de cette date';
      return 'Mission supprimée';
    },
  },
};

function formatTimestamp(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  const timeStr = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin} min`;
  if (diffHour < 24) return `il y a ${diffHour}h`;
  if (diffDay === 1) return `hier à ${timeStr}`;
  if (diffDay < 7) {
    const day = date.toLocaleDateString('fr-FR', { weekday: 'long' });
    return `${day} à ${timeStr}`;
  }
  return `${date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })} à ${timeStr}`;
}

function getDayLabel(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const logDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (logDay.getTime() === today.getTime()) return "Aujourd'hui";
  if (logDay.getTime() === yesterday.getTime()) return 'Hier';
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function groupByDay(logs: ActivityLog[]): Array<{ day: string; entries: ActivityLog[] }> {
  const groups = new Map<string, ActivityLog[]>();
  for (const log of logs) {
    const key = getDayLabel(log.created_at);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(log);
  }
  return Array.from(groups.entries()).map(([day, entries]) => ({ day, entries }));
}

export function TaskActivityHistory({ taskId, occurrenceDate }: TaskActivityHistoryProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    if (hasLoaded) return;
    setIsLoading(true);
    setFetchError(null);
    try {
      let query = supabase
        .from('task_activity_logs')
        .select('*')
        .eq('task_id', taskId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (occurrenceDate) {
        query = query.eq('occurrence_date', occurrenceDate);
      }

      const { data, error } = await query;
      if (error) {
        console.error('[TaskActivityHistory] Failed to load activity logs:', error);
        setFetchError("Impossible de charger l'historique");
      } else if (data) {
        setLogs(data as ActivityLog[]);
      }
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  }, [taskId, occurrenceDate, hasLoaded]);

  useEffect(() => {
    setLogs([]);
    setHasLoaded(false);
    setFetchError(null);
    setIsOpen(false);
  }, [taskId, occurrenceDate]);

  const handleToggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next && !hasLoaded) {
      loadHistory();
    }
  };

  const groups = groupByDay(logs);

  return (
    <div className="border-t border-gray-100 pt-5">
      <button
        type="button"
        onClick={handleToggle}
        className="w-full flex items-center text-sm font-medium text-gray-900 hover:text-gray-700 transition-colors"
      >
        <History size={15} className="mr-1.5 text-gray-400 flex-shrink-0" />
        <span>Historique d'activité</span>
        <ChevronDown
          size={18}
          className={`ml-2 text-gray-500 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[600px] opacity-100 mt-3' : 'max-h-0 opacity-0'
        }`}
      >
        {isLoading && (
          <div className="flex items-center justify-center py-6">
            <span className="w-4 h-4 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
          </div>
        )}

        {!isLoading && hasLoaded && fetchError && (
          <div className="py-5 text-center">
            <AlertCircle size={20} className="mx-auto text-red-400 mb-2" />
            <p className="text-xs text-red-500">{fetchError}</p>
          </div>
        )}

        {!isLoading && hasLoaded && !fetchError && logs.length === 0 && (
          <div className="py-5 text-center">
            <History size={24} className="mx-auto text-gray-300 mb-2" />
            <p className="text-xs text-gray-400">Aucune activité enregistrée</p>
          </div>
        )}

        {!isLoading && logs.length > 0 && (
          <div className="space-y-4 overflow-y-auto max-h-[560px] pr-1">
            {groups.map(({ day, entries }) => (
              <div key={day}>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                  {day}
                </p>
                <div className="relative pl-5">
                  <div className="absolute left-1.5 top-2 bottom-2 w-px bg-gray-100" />
                  <div className="space-y-3">
                    {entries.map((log) => {
                      const actionConfig = ACTION_CONFIG[log.action_type];
                      if (!actionConfig) return null;
                      const ActionIcon = actionConfig.icon;
                      const actorDisplay = log.actor_type === 'system'
                        ? 'Système'
                        : (log.actor_name ?? 'Utilisateur');

                      return (
                        <div key={log.id} className="flex items-start gap-2.5">
                          <div className="relative flex-shrink-0">
                            <div className={`w-3 h-3 rounded-full ${actionConfig.dotColor} border-2 border-white shadow-sm mt-0.5`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start gap-1.5 flex-wrap">
                              <ActionIcon size={12} className="text-gray-400 flex-shrink-0 mt-0.5" />
                              <p className="text-xs text-gray-800 leading-relaxed">
                                <span className="font-medium">{actionConfig.label(log)}</span>
                                {log.actor_type !== 'system' && (
                                  <>
                                    {' '}
                                    <span className="text-gray-500">par </span>
                                    <span className="font-medium text-gray-700">{actorDisplay}</span>
                                  </>
                                )}
                                {log.actor_type === 'system' && (
                                  <span className="inline-flex items-center gap-0.5 ml-1 text-gray-400">
                                    <Bot size={10} />
                                    {actorDisplay}
                                  </span>
                                )}
                              </p>
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5 pl-4">
                              {formatTimestamp(log.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
